/**
 * @name getEditorialPageGrouping
 *
 * @description
 * Determines the analytics page grouping (pageCategory, pagesubCategory, pageDetail)
 * for editorial pages based on the page ID.
 *
 * Migrated from SFRA: common/pageGrouping.isml
 *
 * @rules
 * | Page ID               | pageCategory  | pagesubCategory         | pageDetail  |
 * |-----------------------|---------------|-------------------------|-------------|
 * | homepage              | homepage      | {gender} or "neutral"   | —           |
 * | men-collection        | Discovery     | man                     | —           |
 * | women-collection      | Discovery     | woman                   | —           |
 * | kids-collection       | Discovery     | kids                    | —           |
 * | exclusive-service*    | service       | {page.ID}               | —           |
 * | *shop-the-look*       | Inspiration   | {page.ID}               | Campaign    |
 * | any other PD page     | Inspiration   | {page.ID}               | Editorial   |
 */

export interface PageGrouping {
    pageCategory: string
    pagesubCategory: string
    pageDetail: string
}

/**
 * Returns the analytics page grouping for the given editorial page.
 *
 * @param pageId - The Page Designer page ID (e.g. "homepage", "men-collection", "women-collection")
 * @param gender - Optional gender context for homepage (defaults to "neutral")
 */
export function getEditorialPageGrouping(pageId: string, gender?: string): PageGrouping {
    if (pageId === 'homepage') {
        return {
            pageCategory: 'homepage',
            pagesubCategory: gender || 'neutral',
            pageDetail: '',
        }
    }

    if (pageId === 'men-collection') {
        return {
            pageCategory: 'Discovery',
            pagesubCategory: 'man',
            pageDetail: '',
        }
    }

    if (pageId === 'women-collection') {
        return {
            pageCategory: 'Discovery',
            pagesubCategory: 'woman',
            pageDetail: '',
        }
    }

    if (pageId === 'kids-collection') {
        return {
            pageCategory: 'Discovery',
            pagesubCategory: 'kids',
            pageDetail: '',
        }
    }

    if (pageId === 'exclusive-service' || pageId === 'exclusive-service-appointment') {
        return {
            pageCategory: 'service',
            pagesubCategory: pageId,
            pageDetail: '',
        }
    }

    if (pageId.includes('shop-the-look')) {
        return {
            pageCategory: 'Inspiration',
            pagesubCategory: pageId,
            pageDetail: 'Campaign',
        }
    }

    // Default: all other Page Designer editorial pages
    return {
        pageCategory: 'Inspiration',
        pagesubCategory: pageId,
        pageDetail: 'Editorial',
    }
}

// NOTE: the PDP page grouping (formerly derived client-side by `getProductPageGrouping`)
// is now computed server-side by the SCAPI hook and exposed under `product.c_analytics.pageGrouping`,
// centralizing the datalayer base attributes for both PLP (getCategory) and PDP (getProduct).
// The client-side helper has been removed intentionally.

/**
 * @name escapeAnalyticsPageDetail
 *
 * @description
 * Normalizes the analytics `pageDetail` value before it is pushed to the dataLayer,
 * so PLP and PDP stay consistent. The hook returns the (EN) leaf breadcrumb which may
 * contain a raw ampersand (e.g. `coats_&_jackets`); the dataLayer expects it HTML-escaped
 * (`coats_&amp;_jackets`), matching the long-standing PLP behaviour.
 *
 * @param pageDetail - Raw `pageDetail` value from the hook (or a search keyword).
 * @returns The escaped value, or an empty string when undefined/null.
 */
export function escapeAnalyticsPageDetail(pageDetail?: string | null): string {
    return (pageDetail ?? '').replace(/&/g, '&amp;')
}

/**
 * Returns the Dynamic Yield recommendation context type for the given page.
 *
 * @rules
 * - homepage → 'HOMEPAGE'
 * - all other editorial pages → 'OTHER'
 */
export function getDynamicYieldPageType(pageId: string): string {
    return pageId === 'homepage' ? 'HOMEPAGE' : 'OTHER'
}

/**
 * Returns the Callimacus widget page type derivation for editorial pages.
 *
 * @rules
 * - homepage → 'HOME'
 * - men-collection, women-collection, kids-collection → 'DISCOVERY'
 * - all other → 'PAGEDESIGNER'
 */
export function getWidgetPageType(pageId: string): string {
    if (pageId === 'homepage') return 'HOME'
    if (pageId === 'men-collection' || pageId === 'women-collection' || pageId === 'kids-collection') return 'DISCOVERY'
    return 'PAGEDESIGNER'
}
